import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface PriceCardProps {
  fish: {
    id: string;
    name: string;
    scientificName: string;
    image: string;
    alt: string;
    currentPrice: number;
    previousPrice: number;
    changePercent: number;
    trend: 'up' | 'down' | 'stable';
    availability: 'high' | 'medium' | 'low';
    unit: string;
  };
  onViewDetails: (id: string) => void;
  onSetAlert: (id: string) => void;
}

const PriceCard = ({ fish, onViewDetails, onSetAlert }: PriceCardProps) => {
  const getTrendIcon = () => {
    switch (fish.trend) {
      case 'up':
        return <Icon name="ArrowTrendingUpIcon" size={20} className="text-error" />;
      case 'down':
        return <Icon name="ArrowTrendingDownIcon" size={20} className="text-success" />;
      default:
        return <Icon name="MinusIcon" size={20} className="text-muted-foreground" />;
    }
  };

  const getTrendColor = () => {
    switch (fish.trend) {
      case 'up':
        return 'text-error';
      case 'down':
        return 'text-success';
      default:
        return 'text-muted-foreground';
    }
  };

  const getAvailabilityColor = () => {
    switch (fish.availability) {
      case 'high':
        return 'bg-success/10 text-success';
      case 'medium':
        return 'bg-warning/10 text-warning';
      case 'low':
        return 'bg-error/10 text-error';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-elevation-sm transition-smooth hover:shadow-elevation-md hover-lift overflow-hidden">
      <div className="relative h-48 overflow-hidden bg-muted">
        <AppImage
          src={fish.image}
          alt={fish.alt}
          className="w-full h-full object-cover"
        />
        <div className={`absolute top-3 right-3 px-3 py-1 rounded-full text-xs font-medium ${getAvailabilityColor()}`}>
          {fish.availability.charAt(0).toUpperCase() + fish.availability.slice(1)} Stock
        </div>
      </div>

      <div className="p-4">
        <div className="mb-3">
          <h3 className="font-heading font-semibold text-lg text-foreground mb-1">
            {fish.name}
          </h3>
          <p className="text-xs text-muted-foreground italic">{fish.scientificName}</p>
        </div>

        <div className="flex items-end justify-between mb-4">
          <div>
            <div className="flex items-baseline gap-2 mb-1">
              <span className="font-data font-bold text-2xl text-primary">
                ₹{fish.currentPrice.toFixed(2)}
              </span>
              <span className="text-sm text-muted-foreground">/{fish.unit}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground line-through">
                ₹{fish.previousPrice.toFixed(2)}
              </span>
              <div className="flex items-center gap-1">
                {getTrendIcon()}
                <span className={`font-data text-sm font-medium ${getTrendColor()}`}>
                  {fish.changePercent > 0 ? '+' : ''}{fish.changePercent.toFixed(1)}%
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => onViewDetails(fish.id)}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md font-medium text-sm transition-smooth hover:shadow-elevation-md hover-lift"
          >
            <Icon name="ChartBarIcon" size={16} />
            <span>View Chart</span>
          </button>
          <button
            onClick={() => onSetAlert(fish.id)}
            className="px-3 py-2 bg-muted text-foreground rounded-md transition-smooth hover:bg-border"
            aria-label="Set price alert"
          >
            <Icon name="BellIcon" size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PriceCard;